import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, StateBlock, useToast } from '@w7/ui'
import { useMemo, useState } from 'react'
import { cmApi, type EventTypeMaxLimit } from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { canEditMaxLimits, canSaveAllMaxLimits } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { CmPanel, CmStatus } from '@/features/catalog/CmPanel'

function num(v: string, fallback = 0): number {
  if (v.trim() === '') return fallback
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

/** Lab ng-min: max market should be ≥ max bet when both set. */
export function validateRows(rows: EventTypeMaxLimit[]): string | null {
  for (const row of rows) {
    if (row.minBet < 1) {
      return `${row.eventType.name}: Min Bet must be at least 1`
    }
    if (row.maxBetAmt > 0 && row.maxMarketAmount > 0 && row.maxMarketAmount < row.maxBetAmt) {
      return `${row.eventType.name}: Max Market must be ≥ Max Bet`
    }
    if (
      row.eventType.bookmakerEnabled &&
      row.maxBetBookmaker > 0 &&
      row.maxMarketAmountBookmaker > 0 &&
      row.maxMarketAmountBookmaker < row.maxBetBookmaker
    ) {
      return `${row.eventType.name}: Max Market Bookmaker must be ≥ Max Bet Bookmaker`
    }
  }
  return null
}

export function MaxLimitsPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const canEdit = canEditMaxLimits(me.data?.caller)
  const canSaveAll = canSaveAllMaxLimits(me.data?.caller)
  const [rows, setRows] = useState<EventTypeMaxLimit[]>([])
  const [status, setStatus] = useState('')

  const listQ = useQuery({
    queryKey: ['cm', 'event-type-max-limits'],
    queryFn: async () => {
      const res = await cmApi.listEventTypeMaxLimits(authHeaders())
      setRows(res.limits.map((r) => ({ ...r })))
      return res
    },
  })

  const save = useMutation({
    mutationFn: () => {
      const err = validateRows(rows)
      if (err) throw new Error(err)
      return cmApi.saveEventTypeMaxLimits(authHeaders(), rows)
    },
    onSuccess: (data) => {
      toast.success(`Saved ${data.saved} sport limit row(s)`)
      setStatus(`Saved ${data.saved} row(s)`)
      void qc.invalidateQueries({ queryKey: ['cm', 'event-type-max-limits'] })
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const patch = (sportId: string, key: keyof EventTypeMaxLimit, value: string) => {
    if (!canEdit) return
    setRows((prev) =>
      prev.map((r) => {
        if (r.sportId !== sportId) return r
        if (key === 'eventType' || key === 'sportId' || key === 'id') return r
        return { ...r, [key]: num(value) }
      }),
    )
  }

  const limits = rows.length ? rows : (listQ.data?.limits ?? [])
  const fancyRows = useMemo(
    () => limits.filter((r) => r.eventType.fancyEnabled),
    [limits],
  )

  if (listQ.isLoading && !limits.length) {
    return (
      <StateBlock icon="settings" title="Loading Max Limits…">
        Reading Lab EventTypeMaxLimit rows via CM-API.
      </StateBlock>
    )
  }

  return (
    <CmPanel
      title="Max Limits"
      subtitle="Lab EventTypeMaxLimit config — sport-level min/max for place bet & mock bet"
      actions={
        canSaveAll ? (
          <Button
            type="button"
            variant="primary"
            busy={save.isPending}
            onClick={() => save.mutate()}
          >
            Save All
          </Button>
        ) : null
      }
    >
      {listQ.isError ? (
        <div className="cm-hint" role="alert">
          <p>{(listQ.error as Error).message}</p>
          <Button
            type="button"
            variant="secondary"
            busy={listQ.isFetching}
            onClick={() => void listQ.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <div className="cm-section">
        <h3 className="cm-section-title">Bet Settings</h3>
        <div className="cm-table-wrap">
          <table className="cm-table">
            <thead>
              <tr>
                <th>Event Type</th>
                <th>Min Bet</th>
                <th>Max Bet</th>
                <th>Max Market</th>
                <th>Max Bet Fancy — Commission</th>
                <th>Max Bet Bookmaker — Commission</th>
                <th>Max Bet Bookmaker</th>
                <th>Max Market Bookmaker</th>
              </tr>
            </thead>
            <tbody>
              {limits.map((row) => (
                <tr key={row.sportId}>
                  <th scope="row">{row.eventType.name}</th>
                  <td>
                    <input
                      className="cm-num"
                      type="number"
                      min={1}
                      value={row.minBet}
                      onChange={(e) => patch(row.sportId, 'minBet', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="cm-num"
                      type="number"
                      value={row.maxBetAmt}
                      onChange={(e) => patch(row.sportId, 'maxBetAmt', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="cm-num"
                      type="number"
                      value={row.maxMarketAmount}
                      onChange={(e) => patch(row.sportId, 'maxMarketAmount', e.target.value)}
                    />
                  </td>
                  <td>
                    {row.eventType.fancyEnabled ? (
                      <input
                        className="cm-num"
                        type="number"
                        value={row.maxBetFancyAmountForCommission ?? 0}
                        onChange={(e) =>
                          patch(row.sportId, 'maxBetFancyAmountForCommission', e.target.value)
                        }
                      />
                    ) : null}
                  </td>
                  <td>
                    {row.eventType.bookmakerEnabled ? (
                      <input
                        className="cm-num"
                        type="number"
                        value={row.maxBetBookmakerAmountForCommission ?? 0}
                        onChange={(e) =>
                          patch(
                            row.sportId,
                            'maxBetBookmakerAmountForCommission',
                            e.target.value,
                          )
                        }
                      />
                    ) : null}
                  </td>
                  <td>
                    {row.eventType.bookmakerEnabled ? (
                      <input
                        className="cm-num"
                        type="number"
                        value={row.maxBetBookmaker}
                        onChange={(e) => patch(row.sportId, 'maxBetBookmaker', e.target.value)}
                      />
                    ) : null}
                  </td>
                  <td>
                    {row.eventType.bookmakerEnabled ? (
                      <input
                        className="cm-num"
                        type="number"
                        value={row.maxMarketAmountBookmaker}
                        onChange={(e) =>
                          patch(row.sportId, 'maxMarketAmountBookmaker', e.target.value)
                        }
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cm-section">
        <h3 className="cm-section-title">Fancy Bet Settings</h3>
        {!fancyRows.length && !listQ.isError ? (
          <p className="cm-hint">No fancy-enabled sports yet (Lab shows Cricket when fancyEnabled).</p>
        ) : fancyRows.length ? (
          <div className="cm-table-wrap">
            <table className="cm-table">
              <thead>
                <tr>
                  <th>
                    EVENT
                    <br />
                    TYPE
                  </th>
                  <th>
                    Max Bet
                    <br />
                    Fancy
                    <br />
                    Minor
                  </th>
                  <th>
                    Max Market
                    <br />
                    Fancy
                    <br />
                    Minor
                  </th>
                  <th>
                    Max Bet
                    <br />
                    Fancy
                    <br />
                    Minor1
                  </th>
                  <th>
                    Max Market
                    <br />
                    Fancy
                    <br />
                    Minor1
                  </th>
                  <th>
                    Max Bet
                    <br />
                    Fancy
                    <br />
                    Medium
                  </th>
                  <th>
                    Max Market
                    <br />
                    Fancy
                    <br />
                    Medium
                  </th>
                  <th>
                    Max Bet
                    <br />
                    Fancy
                    <br />
                    Medium1
                  </th>
                  <th>
                    Max Market
                    <br />
                    Fancy
                    <br />
                    Medium1
                  </th>
                  <th>
                    Max Bet
                    <br />
                    Fancy
                    <br />
                    Major
                  </th>
                  <th>
                    Max Market
                    <br />
                    Fancy
                    <br />
                    Major
                  </th>
                  <th>
                    Max Bet
                    <br />
                    Fancy
                    <br />
                    Major1
                  </th>
                  <th>
                    Max Market
                    <br />
                    Fancy
                    <br />
                    Major1
                  </th>
                </tr>
              </thead>
              <tbody>
                {fancyRows.map((row) => (
                  <tr key={`fancy-${row.sportId}`}>
                    <th scope="row">{row.eventType.name}</th>
                    {(
                      [
                        'maxBetFancyMinor',
                        'maxMarketAmountFancyMinor',
                        'maxBetFancyMinor1',
                        'maxMarketAmountFancyMinor1',
                        'maxBetFancyMedium',
                        'maxMarketAmountFancyMedium',
                        'maxBetFancyMedium1',
                        'maxMarketAmountFancyMedium1',
                        'maxBetFancyMajor',
                        'maxMarketAmountFancyMajor',
                        'maxBetFancyMajor1',
                        'maxMarketAmountFancyMajor1',
                      ] as const
                    ).map((key) => (
                      <td key={key}>
                        <input
                          className="cm-num"
                          type="number"
                          value={row[key]}
                          onChange={(e) => patch(row.sportId, key, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {canSaveAll ? (
        <div className="cm-actions">
          <Button
            type="button"
            variant="primary"
            busy={save.isPending || listQ.isLoading}
            disabled={listQ.isError}
            onClick={() => save.mutate()}
          >
            Save All
          </Button>
        </div>
      ) : null}
      <CmStatus value={status} />
    </CmPanel>
  )
}
